<template>
  <div>
    <div class="nk-app-root">
      <div class="nk-main">
        <div class="nk-wrap">
          <Header v-show="!loading_page" />
          <div class="nk-content">
            <div class="container wide-xl">
              <div class="nk-content-inner">
                <Navigation v-show="!loading_page" />
                <div class="nk-content-body" v-show="!loading_page">
                  <div class="nk-content-wrap">
                    <div class="nk-block nk-block-lg">
                      <div class="nk-block-head">
                        <div class="nk-block-head-content d-flex justify-content-between align-items-start">
                          <div>
                            <h4 class="nk-block-title">{{ $t('dm_dashboard_title') }}</h4>
                            <p>{{ $t('dm_dashboard_subtitle') }}</p>
                          </div>
                          <div class="d-flex" style="gap: 8px;">
                            <button v-if="canDmAdmin" class="btn btn-sm btn-outline-primary" @click="runMigrateAuftragnehmer" :disabled="migrating">
                              <em class="icon ni ni-swap"></em> {{ migrating ? $t('dm_dashboard_btn_migrate_auftragnehmer_loading') : $t('dm_dashboard_btn_migrate_auftragnehmer') }}
                            </button>
                            <button v-if="canDmAdmin" class="btn btn-sm btn-outline-primary" @click="runSyncContractors" :disabled="syncing">
                              <em class="icon ni ni-reload-alt"></em> {{ syncing ? $t('dm_dashboard_btn_sync_contractors_loading') : $t('dm_dashboard_btn_sync_contractors') }}
                            </button>
                            <button v-if="canDmAdmin" class="btn btn-sm btn-outline-danger" @click="resetAllData" :disabled="resetting">
                              <em class="icon ni ni-reload"></em> {{ resetting ? $t('dm_dashboard_btn_reset_deleting') : $t('dm_dashboard_btn_reset') }}
                            </button>
                          </div>
                        </div>
                      </div>

                      <!-- Kennzahlen -->
                      <div class="d-flex flex-wrap mb-4" style="gap: 10px;">
                        <div class="kz-chip" v-for="kz in kennzahlen" :key="kz.key" @click="navigateTo(kz.route)">
                          <div class="kz-icon-wrap">
                            <em class="icon ni" :class="kz.icon"></em>
                          </div>
                          <span class="kz-label">{{ kz.label }}</span>
                          <span class="kz-value">{{ getCount(kz.key) }}</span>
                          <span v-if="kz.key === 'anlagen' && overview.counts" class="kz-sub">
                            {{ overview.counts.anlagen_wea || 0 }} WEA &middot; {{ overview.counts.anlagen_pv || 0 }} PV &middot; {{ overview.counts.anlagen_bat || 0 }} BAT
                          </span>
                        </div>
                      </div>

                      <!-- Prüfungsfristen Ampel -->
                      <div class="card card-bordered mb-4" style="margin-top: 1.5rem;">
                        <div class="card-inner">
                          <div class="d-flex justify-content-between align-items-center mb-3">
                            <h6 class="overline-title overline-title-alt mb-0">{{ $t('dm_dashboard_section_inspection_dates') }}</h6>
                            <select class="form-control form-control-sm dm-select" v-model="filterAnlageTyp" @change="loadData">
                              <option value="">{{ $t('dm_common_all_types') }}</option>
                              <option value="WEA">WEA</option>
                              <option value="PV">PV</option>
                              <option value="BAT">BAT</option>
                            </select>
                          </div>

                          <div v-if="hasAmpelData">
                            <div class="row g-gs">
                              <div class="col" v-for="cat in ampelCategories" :key="cat.key">
                                <div class="ampel-card text-center p-3"
                                  :style="{ borderLeft: '4px solid ' + cat.color, cursor: 'pointer' }"
                                  :class="{ 'ampel-card-active': selectedAmpelCategory === cat.key }"
                                  @click="toggleAmpelCategory(cat.key)">
                                  <div class="ampel-count" :style="{ color: cat.color }">{{ getAmpelCount(cat.key) }}</div>
                                  <div class="text-muted small">{{ cat.label }}</div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div v-else class="text-center py-4">
                            <em class="icon ni ni-calendar-check" style="font-size: 2.5rem; color: #ccc;"></em>
                            <p class="text-muted mt-2" v-html="$t('dm_dashboard_no_inspection_dates')"></p>
                          </div>
                        </div>
                      </div>

                      <!-- Prüfungsfristen Timeline (12 Monate) -->
                      <div v-if="hasAmpelData" class="card card-bordered mb-4">
                        <div class="card-inner">
                          <h6 class="overline-title overline-title-alt mb-3">{{ $t('dm_dashboard_timeline_title') }}</h6>
                          <div class="timeline-chart">
                            <div class="timeline-bar-group"
                              v-for="col in timelineColumns" :key="col.key"
                              :class="{ 'timeline-bar-active': selectedTimelineMonth === col.key }"
                              @click="toggleTimelineMonth(col.key)">
                              <div class="timeline-bar-stack" :style="{ height: timelineMaxHeight + 'px' }">
                                <div v-for="seg in col.segments" :key="seg.category"
                                  class="timeline-bar-segment"
                                  :style="{ height: seg.height + 'px', backgroundColor: seg.color }"
                                  :title="seg.count + ' ' + seg.label">
                                </div>
                              </div>
                              <div class="timeline-bar-count" v-if="col.total > 0">{{ col.total }}</div>
                              <div class="timeline-bar-label">{{ col.label }}</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Prüfungen-Liste (bei aktiver Ampel-Kategorie oder Timeline-Monat) -->
                      <div v-if="selectedAmpelCategory || selectedTimelineMonth" class="card card-bordered">
                        <div class="card-inner">
                          <div class="d-flex justify-content-between align-items-center mb-3">
                            <h6 class="overline-title overline-title-alt mb-0">
                              {{ $t('dm_dashboard_section_inspection_dates') }}
                              <span v-if="selectedAmpelCategory" class="badge ml-1" :style="{ backgroundColor: getAmpelColorForCategory(selectedAmpelCategory), color: '#fff' }">
                                {{ getAmpelCategoryLabel(selectedAmpelCategory) }}
                              </span>
                              <span v-if="selectedTimelineMonth" class="badge ml-1" style="background-color: #6576ff; color: #fff;">
                                {{ getTimelineMonthLabel(selectedTimelineMonth) }}
                              </span>
                              <span class="text-muted small ml-1">({{ filteredAnlagen.length }})</span>
                            </h6>
                            <a href="#" class="text-muted small" @click.prevent="clearListFilter">
                              <em class="icon ni ni-cross-sm"></em> {{ $t('dm_common_btn_close') }}
                            </a>
                          </div>
                          <div class="form-control-wrap mb-3">
                            <input type="text" class="form-control" :placeholder="$t('dm_common_quick_filter')" v-model="quickFilterText" />
                          </div>
                          <div class="dm-table-wrap">
                            <table class="dm-dash-table">
                              <thead>
                                <tr>
                                  <th class="dm-table-th th-dot"></th>
                                  <th class="dm-table-th" @click="toggleSort('seriennummer')">
                                    {{ $t('dm_common_col_serial_number') }}
                                    <span v-if="sortField === 'seriennummer'" class="dm-sort-icon">{{ sortAsc ? '&#9650;' : '&#9660;' }}</span>
                                  </th>
                                  <th class="dm-table-th" @click="toggleSort('bezeichnung')">
                                    {{ $t('dm_common_col_bezeichnung') }}
                                    <span v-if="sortField === 'bezeichnung'" class="dm-sort-icon">{{ sortAsc ? '&#9650;' : '&#9660;' }}</span>
                                  </th>
                                  <th class="dm-table-th" @click="toggleSort('faelligkeit')">
                                    {{ $t('dm_common_col_faelligkeit') }}
                                    <span v-if="sortField === 'faelligkeit'" class="dm-sort-icon">{{ sortAsc ? '&#9650;' : '&#9660;' }}</span>
                                  </th>
                                  <th class="dm-table-th th-typ" @click="toggleSort('pruefung_typ')">
                                    {{ $t('dm_common_col_type') }}
                                    <span v-if="sortField === 'pruefung_typ'" class="dm-sort-icon">{{ sortAsc ? '&#9650;' : '&#9660;' }}</span>
                                  </th>
                                  <th class="dm-table-th">{{ $t('dm_common_col_status') }}</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr v-for="(row, idx) in filteredAnlagen" :key="idx" class="dm-table-row" @click="goToAnlage(row)">
                                  <td class="td-dot">
                                    <span class="ampel-dot" :style="{ backgroundColor: getAmpelColorForCategory(row.ampel_category || selectedAmpelCategory) }"></span>
                                  </td>
                                  <td class="sn-cell">{{ row.seriennummer }}</td>
                                  <td>{{ row.bezeichnung || '' }}</td>
                                  <td>{{ row.faelligkeit | moment("DD.MM.YYYY") }}</td>
                                  <td>{{ getPruefungTypLabel(row.pruefung_typ) }}</td>
                                  <td>{{ row.status || '' }}</td>
                                </tr>
                                <tr v-if="filteredAnlagen.length === 0">
                                  <td colspan="6" class="text-center text-muted py-4">{{ $t('dm_common_no_entries_found') }}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>

                      <!-- Letzte Aktivitäten -->
                      <div class="card card-bordered mt-4">
                        <div class="card-inner">
                          <div class="d-flex justify-content-between align-items-center mb-3">
                            <h6 class="overline-title overline-title-alt mb-0">{{ $t('dm_dashboard_section_recent_activity') }}</h6>
                          </div>
                          <div class="d-flex flex-wrap mb-3" style="gap: 8px;">
                            <select class="form-control form-control-sm dm-select" v-model="activityFilterUser">
                              <option value="">{{ $t('dm_dashboard_activity_all_users') }}</option>
                              <option v-for="u in activityUsers" :key="u" :value="u">{{ u }}</option>
                            </select>
                            <select class="form-control form-control-sm dm-select" v-model="activityFilterAction">
                              <option value="">{{ $t('dm_dashboard_activity_all_actions') }}</option>
                              <option value="created">{{ $t('dm_dashboard_activity_created') }}</option>
                              <option value="updated">{{ $t('dm_dashboard_activity_updated') }}</option>
                              <option value="deleted">{{ $t('dm_dashboard_activity_deleted') }}</option>
                              <option value="confirmed">{{ $t('dm_dashboard_activity_confirmed') }}</option>
                              <option value="uploaded">{{ $t('dm_dashboard_activity_uploaded') }}</option>
                            </select>
                            <select class="form-control form-control-sm dm-select" v-model="activityFilterDays">
                              <option value="7">{{ $t('dm_dashboard_activity_7days') }}</option>
                              <option value="30">{{ $t('dm_dashboard_activity_30days') }}</option>
                              <option value="90">{{ $t('dm_dashboard_activity_90days') }}</option>
                              <option value="0">{{ $t('dm_dashboard_activity_all_time') }}</option>
                            </select>
                          </div>
                          <div v-if="filteredActivity.length > 0">
                            <div v-for="(act, idx) in filteredActivity" :key="idx" class="d-flex align-items-start py-2" :class="{ 'border-top': idx > 0 }">
                              <em class="icon ni mr-3 mt-1" :class="getActivityIcon(act.action)" style="font-size: 1.2rem; color: #6576ff;"></em>
                              <div>
                                <span>{{ act.beschreibung || act.description }}</span>
                                <div class="text-muted small">{{ act.datum | moment("DD.MM.YYYY HH:mm") }} <span v-if="act.benutzer">— {{ act.benutzer }}</span></div>
                              </div>
                            </div>
                          </div>
                          <div v-else class="text-center py-4">
                            <em class="icon ni ni-activity-round" style="font-size: 2.5rem; color: #ccc;"></em>
                            <p class="text-muted mt-2">{{ $t('dm_dashboard_no_recent_activity') }}</p>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                  <Footer />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Header from "../../../components/Header.vue";
import Navigation from "../../../components/Navigation.vue";
import Footer from "../../../components/Footer.vue";
import dmPermissionsMixin from "@/mixins/dm-permissions-mixin";
import { getDashboardOverview, getPruefungsfristenDashboard, syncContractors, migrateAuftragnehmer, resetAll } from "@/services/dm.service";

export default {
  name: "DmDashboard",
  components: { Header, Navigation, Footer },
  mixins: [dmPermissionsMixin],
  data() {
    return {
      fullLoader: null,
      resetting: false,
      syncing: false,
      migrating: false,
      overview: {},
      recentActivity: [],
      ampelData: {},
      ampelAnlagen: [],
      selectedAmpelCategory: "",
      selectedTimelineMonth: "",
      filterAnlageTyp: "",
      quickFilterText: "",
      sortField: "seriennummer",
      sortAsc: true,
      activityFilterUser: "",
      activityFilterAction: "",
      activityFilterDays: "30",
    };
  },
  computed: {
    kennzahlen() {
      return [
        { key: "anlagen", label: this.$t('dm_common_anlagen'), icon: "ni-setting", route: "/dm/anlagen" },
        { key: "parks", label: this.$t('dm_common_parks'), icon: "ni-map-pin", route: "/dm/parks" },
        { key: "adressen", label: this.$t('dm_common_adressen'), icon: "ni-users", route: "/dm/adressen" },
        { key: "dokumente", label: this.$t('dm_common_dokumente'), icon: "ni-file-docs", route: "/dm/dokumente" },
      ];
    },
    ampelCategories() {
      return [
        { key: "ueberfaellig", label: this.$t('dm_dashboard_ampel_overdue'), color: "#7C3AED" },
        { key: "rot", label: this.$t('dm_dashboard_ampel_90days'), color: "#EF4444" },
        { key: "orange", label: this.$t('dm_dashboard_ampel_180days'), color: "#F97316" },
        { key: "gelb", label: this.$t('dm_dashboard_ampel_270days'), color: "#24c45f" },
        { key: "gruen", label: this.$t('dm_dashboard_ampel_270plus'), color: "#94a3b8" },
      ];
    },
    filteredAnlagen() {
      var result = this.ampelAnlagen;
      if (this.quickFilterText) {
        var q = this.quickFilterText.toLowerCase();
        result = result.filter(function(a) {
          return (a.seriennummer && a.seriennummer.toLowerCase().indexOf(q) !== -1) ||
            (a.bezeichnung && a.bezeichnung.toLowerCase().indexOf(q) !== -1) ||
            (a.pruefung_typ && a.pruefung_typ.toLowerCase().indexOf(q) !== -1);
        });
      }
      if (this.sortField) {
        var field = this.sortField;
        var asc = this.sortAsc;
        result = result.slice().sort(function(a, b) {
          var va = a[field] || "";
          var vb = b[field] || "";
          va = String(va).toLowerCase();
          vb = String(vb).toLowerCase();
          if (va < vb) return asc ? -1 : 1;
          if (va > vb) return asc ? 1 : -1;
          return 0;
        });
      }
      return result;
    },
    allPruefungen() {
      var all = [];
      var categories = ['ueberfaellig', 'rot', 'orange', 'gelb', 'gruen'];
      var self = this;
      categories.forEach(function(cat) {
        var entries = self.ampelData[cat];
        if (Array.isArray(entries)) {
          entries.forEach(function(e) {
            all.push(Object.assign({}, e, { ampel_category: cat }));
          });
        }
      });
      return all;
    },
    timelineColumns() {
      var now = new Date();
      var columns = [];
      var ampelCatMap = {};
      this.ampelCategories.forEach(function(c) { ampelCatMap[c.key] = c; });

      // Überfällige als erste Spalte
      var overdueEntries = this.allPruefungen.filter(function(p) { return p.ampel_category === 'ueberfaellig'; });
      columns.push({
        key: 'overdue',
        label: this.$t('dm_dashboard_ampel_overdue_short'),
        entries: overdueEntries,
        total: overdueEntries.length,
        segments: overdueEntries.length > 0 ? [{ category: 'ueberfaellig', count: overdueEntries.length, color: '#7C3AED', label: this.$t('dm_dashboard_ampel_overdue'), height: 0 }] : []
      });

      // 12 Monate
      for (var i = 0; i < 12; i++) {
        var monthDate = new Date(now.getFullYear(), now.getMonth() + i, 1);
        var monthKey = monthDate.getFullYear() + '-' + String(monthDate.getMonth() + 1).padStart(2, '0');
        var monthNames = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
        var label = monthNames[monthDate.getMonth()] + ' ' + String(monthDate.getFullYear()).slice(2);

        var monthEntries = this.allPruefungen.filter(function(p) {
          if (!p.faelligkeit || p.ampel_category === 'ueberfaellig') return false;
          var d = new Date(p.faelligkeit);
          return d.getFullYear() === monthDate.getFullYear() && d.getMonth() === monthDate.getMonth();
        });

        // Group by ampel category
        var segments = [];
        var catOrder = ['rot', 'orange', 'gelb', 'gruen'];
        catOrder.forEach(function(cat) {
          var catEntries = monthEntries.filter(function(e) { return e.ampel_category === cat; });
          if (catEntries.length > 0) {
            var catInfo = ampelCatMap[cat];
            segments.push({
              category: cat,
              count: catEntries.length,
              color: catInfo ? catInfo.color : '#999',
              label: catInfo ? catInfo.label : cat,
              height: 0
            });
          }
        });

        columns.push({
          key: monthKey,
          label: label,
          entries: monthEntries,
          total: monthEntries.length,
          segments: segments
        });
      }

      // Calculate bar heights
      var maxTotal = 1;
      columns.forEach(function(c) { if (c.total > maxTotal) maxTotal = c.total; });
      var maxBarHeight = 100;
      columns.forEach(function(c) {
        c.segments.forEach(function(s) {
          s.height = Math.max(4, Math.round((s.count / maxTotal) * maxBarHeight));
        });
      });

      return columns;
    },
    timelineMaxHeight() {
      return 100;
    },
    activityUsers() {
      var users = {};
      this.recentActivity.forEach(function(a) {
        if (a.benutzer) users[a.benutzer] = true;
      });
      return Object.keys(users).sort();
    },
    filteredActivity() {
      var result = this.recentActivity;
      var self = this;
      if (this.activityFilterUser) {
        result = result.filter(function(a) { return a.benutzer === self.activityFilterUser; });
      }
      if (this.activityFilterAction) {
        result = result.filter(function(a) { return a.action === self.activityFilterAction; });
      }
      if (this.activityFilterDays && this.activityFilterDays !== "0") {
        var days = parseInt(this.activityFilterDays);
        var cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - days);
        result = result.filter(function(a) {
          return a.datum && new Date(a.datum) >= cutoff;
        });
      }
      return result;
    },
    loading_page() {
      return this.$store.state.loading_page && this.$store.state.loading_page == "setup";
    },
    hasAmpelData() {
      if (!this.ampelData) return false;
      return Object.keys(this.ampelData).some((k) => {
        var val = this.ampelData[k];
        if (Array.isArray(val)) return val.length > 0;
        if (typeof val === "number") return val > 0;
        return false;
      });
    },
  },
  created() {
    this.loadOverview();
    this.loadData();
  },
  methods: {
    loadOverview() {
      this.showLoader();
      getDashboardOverview()
        .then((d) => {
          this.hideLoader();
          if (d.data) {
            this.overview = d.data;
            this.recentActivity = d.data.recent_activity || [];
          }
        })
        .catch((e) => {
          this.hideLoader();
          this.$store.dispatch("api_error", e);
        });
    },
    loadData() {
      var filters = {};
      if (this.filterAnlageTyp) filters.anlage_typ = this.filterAnlageTyp;
      this.selectedAmpelCategory = "";
      this.ampelAnlagen = [];
      getPruefungsfristenDashboard(filters)
        .then((d) => {
          if (d.data) {
            this.ampelData = d.data.summary || {};
            this.$store.commit("set_dm_dashboard", d.data);
          }
        })
        .catch(() => {});
    },
    loadAmpelAnlagen(category) {
      // Use pruefung entries directly from ampelData instead of fetching anlagen
      var entries = this.ampelData[category];
      if (Array.isArray(entries)) {
        this.ampelAnlagen = entries.map(function(e) {
          return Object.assign({}, e, { ampel_category: category });
        });
      } else {
        this.ampelAnlagen = [];
      }
    },
    getCount(key) {
      if (!this.overview.counts) return "–";
      return this.overview.counts[key] != null ? this.overview.counts[key] : "–";
    },
    getAmpelCount(key) {
      var val = this.ampelData[key];
      if (Array.isArray(val)) return val.length;
      if (typeof val === "number") return val;
      return 0;
    },
    getAmpelColor(ampel) {
      var colors = {
        ueberfaellig: "#7C3AED",
        rot: "#EF4444",
        orange: "#F97316",
        gelb: "#24c45f",
        gruen: "#94a3b8",
      };
      return colors[ampel] || "#999";
    },
    getAmpelColorForCategory(key) {
      var cat = this.ampelCategories.find(function(c) { return c.key === key; });
      return cat ? cat.color : "#999";
    },
    getActivityIcon(action) {
      var icons = {
        created: "ni-plus-circle",
        updated: "ni-edit",
        deleted: "ni-trash",
        confirmed: "ni-check-circle",
        uploaded: "ni-upload",
      };
      return icons[action] || "ni-activity-round";
    },
    toggleSort(field) {
      if (this.sortField === field) {
        this.sortAsc = !this.sortAsc;
      } else {
        this.sortField = field;
        this.sortAsc = true;
      }
    },
    navigateTo(route) {
      this.$router.push(route);
    },
    toggleAmpelCategory(key) {
      this.selectedTimelineMonth = "";
      if (this.selectedAmpelCategory === key) {
        this.selectedAmpelCategory = "";
        this.ampelAnlagen = [];
      } else {
        this.selectedAmpelCategory = key;
        this.quickFilterText = "";
        this.loadAmpelAnlagen(key);
      }
    },
    toggleTimelineMonth(key) {
      this.selectedAmpelCategory = "";
      if (this.selectedTimelineMonth === key) {
        this.selectedTimelineMonth = "";
        this.ampelAnlagen = [];
      } else {
        this.selectedTimelineMonth = key;
        this.quickFilterText = "";
        var col = this.timelineColumns.find(function(c) { return c.key === key; });
        this.ampelAnlagen = col ? col.entries : [];
      }
    },
    clearListFilter() {
      this.selectedAmpelCategory = "";
      this.selectedTimelineMonth = "";
      this.ampelAnlagen = [];
    },
    getTimelineMonthLabel(key) {
      var col = this.timelineColumns.find(function(c) { return c.key === key; });
      return col ? col.label : key;
    },
    getAmpelCategoryLabel(key) {
      var cat = this.ampelCategories.find(function(c) { return c.key === key; });
      return cat ? cat.label : key;
    },
    getPruefungTypLabel(typ) {
      var map = {
        WKP_M: 'dm_anlage_inspection_type_wkp_m',
        WKP_RB: 'dm_anlage_inspection_type_wkp_rb',
        BPW: 'dm_anlage_inspection_type_bpw',
        Druckbehaelter: 'dm_anlage_inspection_type_druckbehaelter',
        Lift_Leiter: 'dm_anlage_inspection_type_lift_leiter',
        AwSV: 'dm_anlage_inspection_type_awsv',
        Blitzschutz: 'dm_anlage_inspection_type_blitzschutz',
        Elektrische_Anlage: 'dm_anlage_inspection_type_elektrische',
        Wechselrichter_Wartung: 'dm_anlage_inspection_type_wechselrichter',
        Thermografie: 'dm_anlage_inspection_type_thermografie',
        Brandschutz: 'dm_anlage_inspection_type_brandschutz',
        BMS_Check: 'dm_anlage_inspection_type_bms',
        Kapazitaetstest: 'dm_anlage_inspection_type_kapazitaetstest',
      };
      var key = map[typ];
      return key ? this.$t(key) : typ || '';
    },
    goToAnlage(row) {
      var id = row && (row.anlage_id || row._id);
      if (id) {
        this.$router.push("/dm/anlage/" + id);
      }
    },
    runMigrateAuftragnehmer() {
      this.migrating = true;
      migrateAuftragnehmer()
        .then((d) => {
          this.migrating = false;
          if (d && d.status === "error") {
            this.$swal(this.$t('dm_common_error'), d.message || this.$t('dm_common_error_unknown'), "error");
            return;
          }
          var data = d.data || {};
          this.$swal(
            this.$t('dm_dashboard_migrate_auftragnehmer_success'),
            this.$t('dm_dashboard_migrate_auftragnehmer_result').replace('{modified}', data.modified || 0),
            "success"
          );
        })
        .catch((e) => {
          this.migrating = false;
          this.$store.dispatch("api_error", e);
        });
    },
    runSyncContractors() {
      this.syncing = true;
      syncContractors()
        .then((d) => {
          this.syncing = false;
          if (d && d.status === "error") {
            this.$swal(this.$t('dm_common_error'), d.message || this.$t('dm_common_error_unknown'), "error");
            return;
          }
          var data = d.data || {};
          this.$swal(
            this.$t('dm_dashboard_sync_contractors_success'),
            this.$t('dm_dashboard_sync_contractors_result')
              .replace('{updated}', data.updated || 0)
              .replace('{processed}', data.processed || 0),
            "success"
          );
          this.loadOverview();
        })
        .catch((e) => {
          this.syncing = false;
          this.$store.dispatch("api_error", e);
        });
    },
    resetAllData() {
      this.$swal({
        title: this.$t('dm_dashboard_confirm_reset_title'),
        html: this.$t('dm_dashboard_confirm_reset_text'),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: this.$t('dm_dashboard_btn_confirm_delete_all'),
        cancelButtonText: this.$t('dm_common_btn_cancel'),
      }).then((result) => {
        if (result.isConfirmed || result.value) {
          this.resetting = true;
          resetAll()
            .then((d) => {
              this.resetting = false;
              if (d && d.status === "error") {
                this.$swal(this.$t('dm_common_error'), d.message || this.$t('dm_common_error_unknown'), "error");
                return;
              }
              var summary = d.data || {};
              var total = Object.values(summary).reduce(function(a, b) { return a + (b || 0); }, 0);
              this.$swal(this.$t('dm_dashboard_success_reset'), total + " " + this.$t('dm_dashboard_success_reset_count'), "success");
              this.loadOverview();
              this.loadData();
            })
            .catch((e) => {
              this.resetting = false;
              this.$store.dispatch("api_error", e);
            });
        }
      });
    },
    showLoader() {
      this.fullLoader = this.$loading.show({
        container: null, canCancel: false, height: 40, width: 40,
        loader: "spinner", color: "#333333", backgroundColor: "#EEF2FE", opacity: 0.6,
      });
    },
    hideLoader() {
      if (this.fullLoader) this.fullLoader.hide();
    },
  },
};
</script>

<style scoped>
.kz-chip {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 8px 16px; border-radius: 8px;
  border: 1px solid #e2e8f0; background: #fff;
  cursor: pointer; transition: border-color 0.2s, box-shadow 0.2s;
}
.kz-chip:hover { border-color: #6576ff; box-shadow: 0 1px 6px rgba(0,0,0,0.06); }
.kz-icon-wrap {
  width: 30px; height: 30px; border-radius: 6px;
  background: #f0f2ff; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.kz-icon-wrap .icon { font-size: 1rem; color: #6576ff; }
.kz-label { font-size: 0.8rem; color: #8094ae; font-weight: 500; text-transform: uppercase; letter-spacing: 0.03em; }
.kz-value { font-size: 1.1rem; font-weight: 700; color: #364a63; }
.kz-sub { font-size: 0.75rem; color: #8094ae; }
.dm-select { width: 160px !important; color: #364a63 !important; font-size: 0.85rem; }
.ampel-card { background: #fafafa; border-radius: 6px; transition: background 0.15s; }
.ampel-card:hover { background: #f0f0f0; }
.ampel-card-active { box-shadow: 0 0 0 2px rgba(101, 118, 255, 0.4); border-radius: 6px; }
.ampel-count { font-size: 1.8rem; font-weight: 700; line-height: 1.2; }

.dm-table-wrap {
  max-height: calc(100vh - 220px);
  overflow: auto;
}

.dm-dash-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.dm-dash-table thead {
  background: #f8f9fa;
  border-bottom: 2px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 1;
}

.dm-table-th {
  padding: 10px 12px;
  text-align: left;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.dm-table-th:hover {
  color: #334155;
}

.th-dot { width: 32px; padding-right: 0; }
.th-typ { width: 60px; }
.th-hersteller { width: 140px; }

.dm-sort-icon {
  font-size: 10px;
  margin-left: 4px;
  color: #E8751A;
}

.dm-dash-table tbody td {
  padding: 10px 12px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
}

.td-dot { width: 32px; padding-right: 0; }

.dm-table-row {
  cursor: pointer;
  transition: background 0.15s;
}

.dm-table-row:hover {
  background: #f8fafc;
}

.sn-cell {
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-weight: 600;
  font-size: 13px;
}

.ampel-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

/* Timeline Chart */
.timeline-chart {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  padding: 8px 0;
  overflow-x: auto;
}

.timeline-bar-group {
  flex: 1;
  min-width: 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  padding: 4px 2px;
  border-radius: 6px;
  transition: background 0.15s;
}

.timeline-bar-group:hover {
  background: #f0f2ff;
}

.timeline-bar-active {
  background: #f0f2ff;
  box-shadow: 0 0 0 2px rgba(101, 118, 255, 0.4);
}

.timeline-bar-stack {
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  justify-content: flex-start;
  width: 28px;
}

.timeline-bar-segment {
  width: 100%;
  border-radius: 3px;
  margin-bottom: 1px;
  min-height: 0;
  transition: height 0.3s ease;
}

.timeline-bar-count {
  font-size: 11px;
  font-weight: 700;
  color: #334155;
  margin-top: 4px;
}

.timeline-bar-label {
  font-size: 10px;
  color: #8094ae;
  white-space: nowrap;
  margin-top: 2px;
}

</style>

<style>
/* DmDashboard Dark Mode - dash-table specific overrides */
.dark-mode .dm-dash-table thead {
  background: #141c26 !important;
  border-bottom-color: #3c4d62 !important;
}
.dark-mode .dm-dash-table tbody td {
  border-bottom-color: #3c4d62 !important;
  color: #b6c6e3 !important;
}
.dark-mode .sn-cell {
  color: #fff !important;
}
.dark-mode .timeline-bar-group:hover,
.dark-mode .timeline-bar-active {
  background: #1e2a3a !important;
}
.dark-mode .timeline-bar-count {
  color: #b6c6e3 !important;
}
.dark-mode .timeline-bar-label {
  color: #8094ae !important;
}
</style>
